"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { getCopiedDropLink } from "@/helpers/waves/drop-copy-link.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useCanShowDropCurationsAction } from "@/hooks/drops/useCanShowDropCurationsAction";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import type { FC } from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import WaveDropActionsAddReaction from "./WaveDropActionsAddReaction";
import WaveDropCurationsActionIcon from "./WaveDropCurationsActionIcon";
import WaveDropCurationsDialog from "./WaveDropCurationsDialog";
import WaveDropActionsMarkUnread from "./WaveDropActionsMarkUnread";
import WaveDropActionsRate from "./WaveDropActionsRate";
import WaveDropMobileMenuBoost from "./WaveDropMobileMenuBoost";
import WaveDropMobileMenuDelete from "./WaveDropMobileMenuDelete";
import WaveDropMobileMenuEdit from "./WaveDropMobileMenuEdit";
import WaveDropMobileMenuSetPinnedDrop from "./WaveDropMobileMenuSetPinnedDrop";
import WaveDropMobileMenuOpen from "./WaveDropMobileMenuOpen";
import WaveDropActionsQuickReact from "./WaveDropActionsQuickReact";
import { useWaveDropLayers } from "./WaveDropLayerContext";
interface WaveDropMobileMenuProps {
  readonly drop: ApiDrop;
  readonly isOpen: boolean;
  readonly showReplyAndQuote: boolean;
  readonly longPressTriggered: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly onReply: () => void;
  readonly onAddReaction: () => void;
  readonly onEdit?: (() => void) | undefined;
  readonly onBoostAnimation?: (() => void) | undefined;
  readonly showOpenOption?: boolean | undefined;
  readonly showCopyOption?: boolean | undefined;
}
type TimeoutRef = {
  current: ReturnType<typeof setTimeout> | null;
};
const clearCurationsDialogTimeout = (timeoutRef: TimeoutRef) => {
  if (timeoutRef.current !== null) {
    globalThis.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }
};
const getIsDirectMessage = (drop: ApiDrop): boolean => {
  const waveDetails = drop.wave as unknown as {
    chat?: {
      scope?: {
        group?: {
          is_direct_message?: boolean | undefined;
        } | undefined;
      } | undefined;
    } | undefined;
  };
  return waveDetails.chat?.scope?.group?.is_direct_message ?? false;
};
const copyDropLinkToClipboard = ({
  drop,
  isMemesWave,
  isTemporaryDrop,
  closeMenu,
  setCopied
}: {
  readonly drop: ApiDrop;
  readonly isMemesWave: (waveId: string | null | undefined) => boolean;
  readonly isTemporaryDrop: boolean;
  readonly closeMenu: () => void;
  readonly setCopied: (copied: boolean) => void;
}) => {
  if (isTemporaryDrop) {
    return;
  }
  const dropLink = getCopiedDropLink({
    drop,
    isDirectMessage: getIsDirectMessage(drop),
    isMemesWave
  });
  if (typeof navigator.clipboard.writeText !== "function") {
    closeMenu();
    return;
  }
  navigator.clipboard.writeText(dropLink).then(() => {
    setCopied(true);
    globalThis.setTimeout(() => setCopied(false), 2000);
  }).catch(() => undefined);
  closeMenu();
};
const openCurationsDialogAfterMenuClose = ({
  timeoutRef,
  closeMenu,
  setIsCurationsDialogOpen
}: {
  readonly timeoutRef: TimeoutRef;
  readonly closeMenu: () => void;
  readonly setIsCurationsDialogOpen: (open: boolean) => void;
}) => {
  closeMenu();
  clearCurationsDialogTimeout(timeoutRef);
  timeoutRef.current = globalThis.setTimeout(() => {
    setIsCurationsDialogOpen(true);
    timeoutRef.current = null;
  }, 250);
};
function WaveDropMobileCopyButton(t0) {
  const $ = _c(9);
  const {
    copied,
    isTemporaryDrop,
    onClick
  } = t0;
  const t1 = `tw-flex tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 ${isTemporaryDrop ? "tw-cursor-default tw-opacity-50" : "active:tw-bg-iron-800"} tw-transition-colors tw-duration-200`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <svg className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = `tw-text-base tw-font-semibold ${copied ? "tw-text-primary-400" : "tw-text-iron-300"}`;
  const t4 = copied ? "Copied!" : "Copy link";
  let t5;
  if ($[1] !== t3 || $[2] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== isTemporaryDrop || $[5] !== onClick || $[6] !== t1 || $[7] !== t5) {
    t6 = <button className={t1} onClick={onClick} disabled={isTemporaryDrop}>{t2}{t5}</button>;
    $[4] = isTemporaryDrop;
    $[5] = onClick;
    $[6] = t1;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function WaveDropMobileMenuAuthenticatedActions(t0) {
  const $ = _c(61);
  const {
    extendedDrop,
    drop,
    showReplyAndQuote,
    isTemporaryDrop,
    onReply,
    onAddReaction,
    dialogZIndexClassName,
    onBoostAnimation,
    showOpenOption,
    showCopyOption,
    copied,
    onCopyToClipboard,
    showCurationsAction,
    onCurationsClick,
    isAuthor,
    showOptions,
    onEdit,
    canSetPinnedDrop,
    canDelete,
    closeMenu
  } = t0;
  let t1;
  if ($[0] !== closeMenu || $[1] !== extendedDrop) {
    t1 = <WaveDropActionsQuickReact drop={extendedDrop} isMobile={true} onReacted={closeMenu} />;
    $[0] = closeMenu;
    $[1] = extendedDrop;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== dialogZIndexClassName || $[4] !== extendedDrop || $[5] !== onAddReaction) {
    t2 = <WaveDropActionsAddReaction drop={extendedDrop} isMobile={true} onAddReaction={onAddReaction} dialogZIndexClassName={dialogZIndexClassName} />;
    $[3] = dialogZIndexClassName;
    $[4] = extendedDrop;
    $[5] = onAddReaction;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== closeMenu || $[8] !== isTemporaryDrop || $[9] !== onReply || $[10] !== showReplyAndQuote) {
    t3 = showReplyAndQuote && <button className={`tw-flex tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 ${isTemporaryDrop ? "tw-cursor-default tw-opacity-50" : "active:tw-bg-iron-800"} tw-transition-colors tw-duration-200`} onClick={() => {
      closeMenu();
      globalThis.setTimeout(() => onReply(), 250);
    }} disabled={isTemporaryDrop}><svg className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M7.49 12L3.74 8.248m0 0l3.75-3.75m-3.75 3.75h16.5V19.5" /></svg><span className="tw-text-base tw-font-semibold tw-text-iron-300">Reply</span></button>;
    $[7] = closeMenu;
    $[8] = isTemporaryDrop;
    $[9] = onReply;
    $[10] = showReplyAndQuote;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== closeMenu || $[13] !== extendedDrop || $[14] !== onBoostAnimation) {
    t4 = <WaveDropMobileMenuBoost drop={extendedDrop} onBoostChange={closeMenu} onBoostAnimation={onBoostAnimation} />;
    $[12] = closeMenu;
    $[13] = extendedDrop;
    $[14] = onBoostAnimation;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== closeMenu || $[17] !== drop || $[18] !== showOpenOption) {
    t5 = showOpenOption && <WaveDropMobileMenuOpen drop={{
      type: DropSize.FULL,
      ...drop,
      stableHash: drop.id,
      stableKey: drop.id
    }} onOpenChange={closeMenu} />;
    $[16] = closeMenu;
    $[17] = drop;
    $[18] = showOpenOption;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== copied || $[21] !== isTemporaryDrop || $[22] !== onCopyToClipboard || $[23] !== showCopyOption) {
    t6 = showCopyOption && <WaveDropMobileCopyButton copied={copied} isTemporaryDrop={isTemporaryDrop} onClick={onCopyToClipboard} />;
    $[20] = copied;
    $[21] = isTemporaryDrop;
    $[22] = onCopyToClipboard;
    $[23] = showCopyOption;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== onCurationsClick || $[26] !== showCurationsAction) {
    t7 = showCurationsAction && <button type="button" onClick={onCurationsClick} className="tw-flex tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 tw-text-left tw-transition-colors tw-duration-200 active:tw-bg-iron-800"><WaveDropCurationsActionIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" /><span className="tw-text-base tw-font-semibold tw-text-iron-300">Curate</span></button>;
    $[25] = onCurationsClick;
    $[26] = showCurationsAction;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== closeMenu || $[29] !== drop) {
    t8 = <WaveDropActionsMarkUnread drop={drop} isMobile={true} onMarkUnread={closeMenu} />;
    $[28] = closeMenu;
    $[29] = drop;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== closeMenu || $[32] !== drop || $[33] !== isAuthor) {
    t9 = !isAuthor && <WaveDropActionsRate drop={drop} isMobile={true} onRated={closeMenu} />;
    $[31] = closeMenu;
    $[32] = drop;
    $[33] = isAuthor;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== closeMenu || $[36] !== drop || $[37] !== onEdit || $[38] !== showOptions) {
    t10 = showOptions && onEdit && drop.drop_type !== ApiDropType.Participatory && <WaveDropMobileMenuEdit drop={drop} onEdit={onEdit} onEditTriggered={closeMenu} />;
    $[35] = closeMenu;
    $[36] = drop;
    $[37] = onEdit;
    $[38] = showOptions;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== canSetPinnedDrop || $[41] !== closeMenu || $[42] !== drop) {
    t11 = canSetPinnedDrop && <WaveDropMobileMenuSetPinnedDrop drop={drop} onPinnedDropSet={closeMenu} />;
    $[40] = canSetPinnedDrop;
    $[41] = closeMenu;
    $[42] = drop;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== canDelete || $[45] !== closeMenu || $[46] !== drop) {
    t12 = canDelete && <WaveDropMobileMenuDelete drop={drop} onDropDeleted={closeMenu} />;
    $[44] = canDelete;
    $[45] = closeMenu;
    $[46] = drop;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t1 || $[49] !== t10 || $[50] !== t11 || $[51] !== t12 || $[52] !== t2 || $[53] !== t3 || $[54] !== t4 || $[55] !== t5 || $[56] !== t6 || $[57] !== t7 || $[58] !== t8 || $[59] !== t9) {
    t13 = <>{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}</>;
    $[48] = t1;
    $[49] = t10;
    $[50] = t11;
    $[51] = t12;
    $[52] = t2;
    $[53] = t3;
    $[54] = t4;
    $[55] = t5;
    $[56] = t6;
    $[57] = t7;
    $[58] = t8;
    $[59] = t9;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  return t13;
}
const WaveDropMobileMenu: FC<WaveDropMobileMenuProps> = t0 => {
  const $ = _c(67);
  const {
    drop,
    isOpen,
    showReplyAndQuote,
    longPressTriggered,
    setOpen,
    onReply,
    onAddReaction,
    onEdit,
    onBoostAnimation,
    showOpenOption: t1,
    showCopyOption: t2
  } = t0;
  const showOpenOption = t1 === undefined ? true : t1;
  const showCopyOption = t2 === undefined ? true : t2;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave
  } = useSeizeSettings();
  let t3;
  if ($[0] !== drop.id) {
    t3 = drop.id.startsWith("temp-");
    $[0] = drop.id;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isTemporaryDrop = t3;
  const {
    canDelete,
    canSetPinnedDrop
  } = useDropInteractionRules(drop);
  const {
    mobileMenuZIndexClassName,
    mobileDialogZIndexClassName
  } = useWaveDropLayers();
  let t4;
  if ($[2] !== drop) {
    t4 = {
      ...drop,
      type: DropSize.FULL,
      stableKey: drop.id,
      stableHash: drop.id
    };
    $[2] = drop;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const extendedDrop = t4;
  const [copied, setCopied] = useState(false);
  const [isCurationsDialogOpen, setIsCurationsDialogOpen] = useState(false);
  const curationsDialogTimeoutRef = useRef(null);
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => () => {
      clearCurationsDialogTimeout(curationsDialogTimeoutRef);
    };
    t6 = [];
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let closeMenu;
  let connectedProfileHandle;
  let copyToClipboard;
  let isAuthor;
  let showOptions;
  if ($[6] !== activeProfileProxy || $[7] !== connectedProfile || $[8] !== drop || $[9] !== isMemesWave || $[10] !== isTemporaryDrop || $[11] !== setOpen) {
    copyToClipboard = () => copyDropLinkToClipboard({
      drop,
      isMemesWave,
      isTemporaryDrop,
      closeMenu,
      setCopied
    });
    isAuthor = connectedProfile?.handle === drop.author.handle && !activeProfileProxy;
    connectedProfileHandle = connectedProfile?.handle ?? null;
    let t7;
    if ($[17] !== activeProfileProxy || $[18] !== connectedProfileHandle || $[19] !== drop.author.handle || $[20] !== drop.id) {
      t7 = connectedProfileHandle !== null && !activeProfileProxy && !drop.id.startsWith("temp-") && connectedProfileHandle === drop.author.handle;
      $[17] = activeProfileProxy;
      $[18] = connectedProfileHandle;
      $[19] = drop.author.handle;
      $[20] = drop.id;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    showOptions = t7;
    closeMenu = () => setOpen(false);
    $[6] = activeProfileProxy;
    $[7] = connectedProfile;
    $[8] = drop;
    $[9] = isMemesWave;
    $[10] = isTemporaryDrop;
    $[11] = setOpen;
    $[12] = closeMenu;
    $[13] = connectedProfileHandle;
    $[14] = copyToClipboard;
    $[15] = isAuthor;
    $[16] = showOptions;
  } else {
    closeMenu = $[12];
    connectedProfileHandle = $[13];
    copyToClipboard = $[14];
    isAuthor = $[15];
    showOptions = $[16];
  }
  const showGuestCopyOnly = connectedProfileHandle === null;
  const t7 = drop.wave.authenticated_user_admin === true;
  const t8 = (isOpen || isCurationsDialogOpen) && connectedProfileHandle !== null;
  let t9;
  if ($[22] !== drop.id || $[23] !== isTemporaryDrop || $[24] !== t7 || $[25] !== t8) {
    t9 = {
      dropId: drop.id,
      isTemporaryDrop,
      isWaveAdmin: t7,
      enabled: t8
    };
    $[22] = drop.id;
    $[23] = isTemporaryDrop;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const showCurationsAction = useCanShowDropCurationsAction(t9);
  let t10;
  if ($[27] !== closeMenu) {
    t10 = () => openCurationsDialogAfterMenuClose({
      timeoutRef: curationsDialogTimeoutRef,
      closeMenu,
      setIsCurationsDialogOpen
    });
    $[27] = closeMenu;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const handleCurationsClick = t10;
  const t11 = `tw-grid tw-grid-cols-1 ${longPressTriggered && "tw-select-none"}`;
  let t12;
  if ($[29] !== canDelete || $[30] !== canSetPinnedDrop || $[31] !== closeMenu || $[32] !== copied || $[33] !== copyToClipboard || $[34] !== drop || $[35] !== extendedDrop || $[36] !== handleCurationsClick || $[37] !== isAuthor || $[38] !== isTemporaryDrop || $[39] !== mobileDialogZIndexClassName || $[40] !== onAddReaction || $[41] !== onBoostAnimation || $[42] !== onEdit || $[43] !== onReply || $[44] !== showCopyOption || $[45] !== showCurationsAction || $[46] !== showGuestCopyOnly || $[47] !== showOpenOption || $[48] !== showOptions || $[49] !== showReplyAndQuote) {
    t12 = showGuestCopyOnly ? showCopyOption && <WaveDropMobileCopyButton copied={copied} isTemporaryDrop={isTemporaryDrop} onClick={copyToClipboard} /> : <WaveDropMobileMenuAuthenticatedActions extendedDrop={extendedDrop} drop={drop} showReplyAndQuote={showReplyAndQuote} isTemporaryDrop={isTemporaryDrop} onReply={onReply} onAddReaction={onAddReaction} dialogZIndexClassName={mobileDialogZIndexClassName} onBoostAnimation={onBoostAnimation} showOpenOption={showOpenOption} showCopyOption={showCopyOption} copied={copied} onCopyToClipboard={copyToClipboard} showCurationsAction={showCurationsAction} onCurationsClick={handleCurationsClick} isAuthor={isAuthor} showOptions={showOptions} onEdit={onEdit} canSetPinnedDrop={canSetPinnedDrop} canDelete={canDelete} closeMenu={closeMenu} />;
    $[29] = canDelete;
    $[30] = canSetPinnedDrop;
    $[31] = closeMenu;
    $[32] = copied;
    $[33] = copyToClipboard;
    $[34] = drop;
    $[35] = extendedDrop;
    $[36] = handleCurationsClick;
    $[37] = isAuthor;
    $[38] = isTemporaryDrop;
    $[39] = mobileDialogZIndexClassName;
    $[40] = onAddReaction;
    $[41] = onBoostAnimation;
    $[42] = onEdit;
    $[43] = onReply;
    $[44] = showCopyOption;
    $[45] = showCurationsAction;
    $[46] = showGuestCopyOnly;
    $[47] = showOpenOption;
    $[48] = showOptions;
    $[49] = showReplyAndQuote;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  let t13;
  if ($[51] !== t11 || $[52] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[51] = t11;
    $[52] = t12;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== isOpen || $[55] !== mobileMenuZIndexClassName || $[56] !== setOpen || $[57] !== t13) {
    t14 = createPortal(<CommonDropdownItemsMobileWrapper isOpen={isOpen} setOpen={setOpen} zIndexClassName={mobileMenuZIndexClassName}>{t13}</CommonDropdownItemsMobileWrapper>, document.body);
    $[54] = isOpen;
    $[55] = mobileMenuZIndexClassName;
    $[56] = setOpen;
    $[57] = t13;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== drop.id || $[60] !== drop.wave || $[61] !== isCurationsDialogOpen || $[62] !== showCurationsAction) {
    t15 = showCurationsAction && <WaveDropCurationsDialog dropId={drop.id} wave={drop.wave} isOpen={isCurationsDialogOpen} onClose={() => setIsCurationsDialogOpen(false)} />;
    $[59] = drop.id;
    $[60] = drop.wave;
    $[61] = isCurationsDialogOpen;
    $[62] = showCurationsAction;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== t14 || $[65] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  return t16;
};
export default WaveDropMobileMenu;